package com.eladfinish.bible.contest.screens.americanQuestions.usecases

import com.eladfinish.bible.contest.common.ChaptersRepository
import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.screens.americanQuestions.AmericanQuestionsRepository
import com.eladfinish.bible.contest.screens.americanQuestions.model.AmericanQuestionItem
import com.eladfinish.bible.contest.screens.americanQuestions.model.AmericanQuestionSheetEntry
import com.eladfinish.bible.contest.utils.gematriaWord
import kotlinx.serialization.json.Json

class GetAmericanQuestionsUseCase(
    private val repository: AmericanQuestionsRepository,
) {

    /**
     * Fetches questions from the network, processes them, and returns:
     * - the JSON string to persist to file (serialised [AmericanQuestionSheetEntry] list)
     * - the filtered [AmericanQuestionItem] list ready for the game
     */
    suspend fun fetch(
        filterByMikud: Boolean,
        mikudFilenames: Set<String>,
    ): Result<Pair<String, List<AmericanQuestionItem>>> =
        repository.fetchEntries().map { entries ->
            val json = Json.encodeToString(entries)
            val items = processQuestions(
                items = entries,
                filterByMikud = filterByMikud,
                mikudFilenames = mikudFilenames,
            )
            json to items
        }

    /**
     * Processes a list of [AmericanQuestionSheetEntry] (e.g. loaded from the local cache file)
     * into [AmericanQuestionItem] list, applying mikud filtering if required.
     */
    fun processQuestions(
        items: List<AmericanQuestionSheetEntry>,
        filterByMikud: Boolean,
        mikudFilenames: Set<String>,
    ): List<AmericanQuestionItem> {
        val booksMap = buildBooksMap()
        var total = 0

        return items.mapNotNull { entry ->
            val bookPrefix = booksMap[entry.bookName]
                ?: return@mapNotNull null

            val chapterStr = calculateChapter(bookPrefix, entry.chapter)
            val filename = "$bookPrefix-$chapterStr.txt"
            val isIncluded = mikudFilenames.contains(filename)

            if (!filterByMikud || isIncluded) total++

            AmericanQuestionItem(
                num = total,
                question = entry.question,
                options = entry.options,
                correctOption = entry.correctOption,
                sourceBook = bookPrefix,
                sourceChapter = chapterStr,
                author = "${entry.firstName} ${entry.lastName}",
                isIncluded = isIncluded,
            )
        }.filter { !filterByMikud || it.isIncluded }
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    private fun buildBooksMap(): Map<String, String> {
        val hebrewNames = BooksRepository.bookNamesHebrew
        val englishNames = BooksRepository.bookNamesEnglish
        return buildMap {
            for (i in hebrewNames.indices) {
                this[hebrewNames[i]] = ChaptersRepository.getBookPrefix(
                    number = i + 1,
                    bookName = englishNames[i],
                )
            }
        }
    }

    private fun calculateChapter(bookPrefix: String, sourceChapter: String): String {
        if (sourceChapter.isEmpty()) return ""
        val chapterNum = sourceChapter.replaceRegex("['\"]", "").gematriaWord()
        return ChaptersRepository.padNumber(bookNameEnglish = bookPrefix, chapterNum = chapterNum)
    }
}
